import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from "react-redux";
import { AppActionCreators, IApplicationState, IRccapReportDetailResponse, RccapActionCreators } from '@nkr/store/index';
import { IBaseResponse, IRccapReportDetail } from '@nkr/models/index';
import { GridApi, ColumnApi, GridReadyEvent, RowDataChangedEvent, FirstDataRenderedEvent, SortChangedEvent, ProcessCellForExportParams, CsvExportParams } from 'ag-grid-community';
import { Button, ButtonGroup, FormControl, InputGroup } from 'react-bootstrap';
import ConfirmDelete from './confirm-delete';
import { toast } from 'react-toastify';
import { downloadFile } from '@nkr/services/download-file';
import MaskedInput from 'react-maskedinput';
import Grid from './grid';

enum ReportType {
    LabDirectors = 1,
    Coordinators = 2,
    Billing = 3
}

const getInitialDates = (report: ReportType) => {
    if (report == ReportType.Billing) {
        let previousMonth = moment().add(-1, 'month');
        let sd = previousMonth.clone().date(1);
        let ed = sd.clone().date(previousMonth.daysInMonth());

        return { startDate: sd.format('MM/DD/YYYY'), endDate: ed.format('MM/DD/YYYY') };
    } else {
        let startDate = moment().subtract(1, 'quarter').startOf('quarter').format('MM/DD/YYYY');
        let endDate = moment().subtract(1, 'quarter').endOf('quarter').format('MM/DD/YYYY');

        return { startDate, endDate };
    }
}

interface IOwnProps {
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRccapReportDetail: (report: number, startDate: string, endDate: string) => Promise<IRccapReportDetailResponse>,
    getRccapReportDetailPdf: (report: number, startDate: string, endDate: string, rccapId: number) => Promise<any>,
    deleteRccap: (id: number) => Promise<IBaseResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: IRccapReportDetail[],
    report: ReportType,
    startDate: string,
    endDate: string,
    delete: {
        show: boolean,
        data: IRccapReportDetail
    }
}

class RccapDetail extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        report: ReportType.Coordinators,
        startDate: getInitialDates(ReportType.Coordinators).startDate,
        endDate: getInitialDates(ReportType.Coordinators).endDate,
        delete: {
            show: false,
            data: null
        }
    }

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.gridColumnApi = e.columnApi;

        e.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    }

    componentDidMount = () => {
        this.getData('Getting RCCAPs, one moment please...');
    }

    getData = (message: string) => {
        let { report, startDate, endDate } = this.state;

        this.props.showLoading(message).then(() => {
            this.props.getRccapReportDetail(report, startDate, endDate).then(response => {
                this.setState({
                    data: response.data.results
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    getPdf = (rccapId: number) => {
        let { report, startDate, endDate } = this.state;

        this.props.showLoading('Getting RCCAP PDF, one moment please...').then(() => {
            this.props.getRccapReportDetailPdf(report, startDate, endDate, rccapId)
                .then(response => {
                    let fileName = `rccap-detail-${startDate}-${endDate}.pdf`;

                    if (rccapId > 0) {
                        let rccap = this.state.data.find(x => x.id == rccapId);

                        fileName = `rccap-detail-swap_${rccap.swapNumber}.pdf`;
                    }

                    downloadFile(response.data, fileName, 'application/pdf');
                })
                .catch(error => {
                    alert('Unable to generate PDF: ' + error.message);
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleReportChange = (report: number) => {
        let d = getInitialDates(report);

        this.setState({
            report,
            startDate: d.startDate,
            endDate: d.endDate
        }, () => {
            this.getData('Refreshing RCCAP list, one moment please...');
        });
    }

    handlePreviousNext = (next: boolean) => {
        let amount = next ? 1 : -1;

        if (this.state.report == ReportType.Billing) {
            let sd = moment(this.state.startDate, 'MM/DD/YYYY').add(amount, 'month');
            let ed = moment(this.state.endDate, 'MM/DD/YYYY').add(amount, 'month');

            ed = ed.date(ed.daysInMonth());

            this.setState({
                startDate: sd.format('MM/DD/YYYY'),
                endDate: ed.format('MM/DD/YYYY')
            });
        } else {
            let startDate = moment(this.state.startDate, 'MM/DD/YYYY').add(amount, 'quarter').format('MM/DD/YYYY');
            let endDate = moment(this.state.endDate, 'MM/DD/YYYY').add(amount, 'quarter').format('MM/DD/YYYY');

            this.setState({
                startDate,
                endDate
            });
        }
    }

    handleStartDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            startDate: e.currentTarget.value
        });
    }

    handleEndDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            endDate: e.currentTarget.value
        });
    }

    handleSubmit = () => {
        this.getData('Refreshing RCCAP list, one moment please...');
    }

    handleCreatePdf = () => {
        this.getPdf(0);
    }

    handleAction = (rccapId: number, eventKey: any) => {
        if (eventKey == 0) {
            window.open('/admin/rccap/' + rccapId + '?origin=new-rccap-detail', '_blank')
        }

        if (eventKey == 1) {
            this.setState({
                delete: {
                    show: true,
                    data: this.state.data.find(x => x.id == rccapId)
                }
            });
        }

        if (eventKey == 2) {
            this.getPdf(rccapId);
        }
    }

    handleDeleteYes = () => {
        let id = this.state.delete.data.id;

        this.setState({
            delete: {
                show: false,
                data: null
            }
        }, () => {
            this.props.showLoading('Deleting RCCAP, one moment please...').then(() => {
                this.props.deleteRccap(id).then(response => {
                    if (response.data.success) {
                        toast.success('RCCAP deleted successfully');
                    }

                    this.props.hideLoading();
                });
            });
        });
    }

    handleDeleteNo = () => {
        this.setState({
            delete: {
                show: false,
                data: null
            }
        });
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'rccap-detail.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'Rank') {
                    return params.node.rowIndex + 1;
                } else if (colDef.colId == 'failDate') {
                    return moment(params.value).format('MM/DD/YYYY');
                }

                if (colDef.colId == 'billable') {
                    let data = params.value;

                    if (data == 1) {
                        return 'Yes';
                    }

                    if (data == 2) {
                        return 'No';
                    }
                    return 'Unknown';
                }

                if (colDef.colId == 'donorCenter') {

                }

                return params.value;
            }
        };

        this.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div>
                        <div style={{ fontSize: 28, fontWeight: 500 }}>RCCAP Detail</div>
                    </div>
                    <div>
                        <Button onClick={this.handleExport}>Export CSV</Button>
                    </div>
                </div>

                <div className='row'>
                    <div className="col-xs-12">
                        <div className='well well-sm'>
                            <div className="row">
                                <div className='col-xs-12 col-sm-4'>
                                    <ButtonGroup>
                                        <button className={this.state.report == 1 ? "btn btn-primary active center-type" : "btn btn-default center-type"} onClick={() => this.handleReportChange(1)}>Lab Directors</button>
                                        <button className={this.state.report == 2 ? "btn btn-primary active center-type" : "btn btn-default center-type"} onClick={() => this.handleReportChange(2)}>Coordinators</button>
                                        <button className={this.state.report == 3 ? "btn btn-primary active center-type" : "btn btn-default center-type"} onClick={() => this.handleReportChange(3)}>Billing</button>
                                    </ButtonGroup>
                                </div>
                                <div className='col-xs-12 col-sm-4'>
                                    <div>
                                        <InputGroup>
                                            <InputGroup.Button>
                                                <Button title="Previous Period" onClick={() => this.handlePreviousNext(false)}><span className="glyphicon glyphicon-chevron-left"></span></Button>
                                            </InputGroup.Button>
                                            <MaskedInput
                                                mask='11/11/1111'
                                                className='form-control'
                                                value={this.state.startDate}
                                                onChange={this.handleStartDateChange}
                                            />
                                            <InputGroup.Addon style={{ padding: 0 }}>{' '}</InputGroup.Addon>
                                            <MaskedInput
                                                mask='11/11/1111'
                                                className='form-control'
                                                value={this.state.endDate}
                                                onChange={this.handleEndDateChange}
                                            />
                                            <InputGroup.Button>
                                                <Button title='Next Period' onClick={() => this.handlePreviousNext(true)}><span className="glyphicon glyphicon-chevron-right"></span></Button>
                                            </InputGroup.Button>
                                        </InputGroup>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-sm-3 col-sm-offset-1">
                                    <div className='row'>
                                        <div className='col-xs-6 col-sm-6'>
                                            <Button block onClick={this.handleSubmit}>Update</Button>
                                        </div>
                                        <div className='col-xs-6 col-sm-6'>
                                            <Button block onClick={this.handleCreatePdf}>Create PDFs</Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Grid
                    data={this.state.data}
                    onGridReady={this.onGridReady}
                    onFirstDataRendered={this.onFirstDataRendered}
                    onRowDataChanged={this.onRowDataChanged}
                    onSortChanged={this.onSortChanged}
                    onAction={this.handleAction}
                />

                <ConfirmDelete
                    show={this.state.delete.show}
                    data={this.state.delete.data}
                    onYes={this.handleDeleteYes}
                    onNo={this.handleDeleteNo}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRccapReportDetail: (report, startDate, endDate) => {
            return dispatch(RccapActionCreators.getRccapReportDetail(report, startDate, endDate));
        },
        getRccapReportDetailPdf: (report, startDate, endDate, rccapId) => {
            return dispatch(RccapActionCreators.getRccapReportDetailPdf(report, startDate, endDate, rccapId));
        },
        deleteRccap: (id) => {
            return dispatch(RccapActionCreators.deleteRccap(id));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(RccapDetail);